import { addDays } from 'date-fns';
import { format } from 'date-fns';
import * as dateFnsTz from 'date-fns-tz';
import { useMemo } from 'react';
import { TELEPORT_LS_KEY } from '../../Dogfood/Dogfood';
import { FilterChoice, SearchDate } from '../../common/components/SearchHeader/types/FilterChoice';
import { useLocalStorage } from '../../common/hooks/useLocalStorage';
import { useRouter } from '../../common/hooks/useRouter';
import { FreeTextSearchVariables, PracticeTag } from '../../common/schema/hannibal/types.generated';
import { useServerTimestamp } from '../../common/services/serverTimestamp';
import { filterNulls } from '../../common/util/filterNulls';
import { now } from '../../common/util/now';
import { run } from '../../common/util/run';
import { SuburbType } from '../../corporate/search/LocationBox/SuburbType';
import { PAGE_SIZE } from '../constants';
import { locationFiltersFromChoice } from './locationFiltersFromChoice';
import { profileFiltersFromChoice } from './profileFiltersFromChoice';

const DAY_FORMAT = 'yyyy-MM-dd';

const EARLY_TIMEZONE = 'Australia/Sydney';
const LATE_TIMEZONE = 'Australia/Perth';

export const useVariables = (
  filterChoice: FilterChoice,
  perPage = PAGE_SIZE,
  searchRadius?: number,
): FreeTextSearchVariables => {
  const frozenNow = useServerTimestamp();
  const includeTestProfiles = Boolean(useRouter()?.query?.includeTestProfiles);

  // dogfood forced location
  const [teleportLocation] = useLocalStorage<SuburbType | null>(TELEPORT_LS_KEY, null);

  return useMemo(
    () =>
      makeSearchVariables({
        filterChoice,
        frozenNow,
        perPage,
        includeTestProfiles,
        searchRadius,
        teleportLocation,
      }),
    [filterChoice, frozenNow, perPage, includeTestProfiles, searchRadius, teleportLocation],
  );
};

const makeNewAppointmentRange = (frozenNow: number, dateFilter?: SearchDate) => {
  if (!dateFilter) return { appointmentDateStart: null, appointmentDateEnd: null };

  if (typeof dateFilter === 'object') {
    return {
      appointmentDateStart: dateFilter.start,
      appointmentDateEnd: dateFilter.end ?? dateFilter.start,
    };
  }

  const today = run(() => {
    try {
      return dateFnsTz.toDate(frozenNow, { timeZone: LATE_TIMEZONE });
    } catch (error) {
      /** Fallback for IE11 */
      console.error(error);
      return new Date(now());
    }
  });

  if (dateFilter === 'Today')
    return {
      appointmentDateStart: renderDay(today, LATE_TIMEZONE),
      appointmentDateEnd: renderDay(today, EARLY_TIMEZONE),
    };

  if (dateFilter === 'Tomorrow') {
    const tomorrow = addDays(today, 1);
    return {
      appointmentDateStart: renderDay(tomorrow, LATE_TIMEZONE),
      appointmentDateEnd: renderDay(tomorrow, EARLY_TIMEZONE),
    };
  }

  const inAWeek = addDays(today, 6);
  return {
    appointmentDateStart: renderDay(today, LATE_TIMEZONE),
    appointmentDateEnd: renderDay(inAWeek, EARLY_TIMEZONE),
  };
};

const renderDay = (date: number | Date, timezone: string) => {
  try {
    return dateFnsTz.format(dateFnsTz.utcToZonedTime(date, timezone), DAY_FORMAT);
  } catch (error) {
    /** Fallback for IE11 */
    console.error(error);
    return format(date, DAY_FORMAT);
  }
};

export interface VariablesArgs {
  filterChoice: FilterChoice;
  frozenNow: number;
  perPage?: number;
  includeTestProfiles?: boolean;
  searchRadius?: number;
  teleportLocation?: SuburbType | null;
}

export const makeSearchVariables = ({
  filterChoice,
  frozenNow,
  perPage,
  includeTestProfiles = false,
  searchRadius,
  teleportLocation,
}: VariablesArgs) => {
  const dateFilter = filterChoice.onlineAppointments?.date;

  /** In the special case of a location+specialty filter, the search text is purely decorative and is ignored. */
  const profile = profileFiltersFromChoice(filterChoice);

  const location = locationFiltersFromChoice(filterChoice.location, searchRadius, teleportLocation);

  const { flag, specialOffer, acceptsNewPatients, fluVaxAppointmentType } = filterChoice;

  const teleEnabled = flag === 'telehealth';

  const rawTags: PracticeTag[] = filterNulls([
    filterChoice.topic?.bulkBillingGp ? PracticeTag.BulkBilling : null,
    teleEnabled ? PracticeTag.TeleEnabled : null,
    specialOffer ? PracticeTag.SpecialOffer : null,
    acceptsNewPatients ? PracticeTag.AcceptsNewPatients : null,
    fluVaxAppointmentType ? PracticeTag.HasFluVax : null,
  ]);

  const tags = rawTags.length > 0 ? rawTags : undefined;

  const hideTeleAggregators = filterChoice.hideTeleAggregators;

  const rawExcludeTags: PracticeTag[] = filterNulls([
    hideTeleAggregators ? PracticeTag.Telehealth : null,
  ]);

  const excludeTags = rawExcludeTags.length > 0 ? rawExcludeTags : undefined;

  const { appointmentDateStart, appointmentDateEnd } = makeNewAppointmentRange(
    frozenNow,
    dateFilter,
  );

  const time = filterChoice.onlineAppointments?.time;

  const variables = {
    appointmentDateEnd,
    appointmentDateStart,
    first: perPage ?? PAGE_SIZE,
    location: location,
    profile: profile,
    query: filterChoice.text ?? '',
    specialist: filterChoice.topic?.specialist,
    tags,
    excludeTags,
    time,
    includeTestProfiles,
  };

  return variables;
};
